import queryString from 'query-string';
import {fetch} from 'whatwg-fetch';
import {refreshToken} from '@requests/login';

import {setIsLogin} from '@componentDP/js_react/redux/actions/login';
import StorageToken from '@services/storage_token';
import {store} from '../redux/store';
import {refreshTokenProvider} from '../services/auth';
import {sentryInstance} from '../services/sentry';
import LanguageService from "@services/language/LanguageService";

const refreshTokenProviderInstance = new refreshTokenProvider();

const tryRefreshToken = async (refreshTokenProviderInstance, curriedResultFn) => {
    const refreshTokenPromise = refreshTokenProviderInstance.getRefreshTokenPromise();
    if (refreshTokenPromise) {
        await refreshTokenProviderInstance.waitPromiseDone();
        return await curriedResultFn();
    }
    refreshTokenProviderInstance.setRefreshTokenPromise(refreshToken());
    const refreshTokenResult = await refreshTokenProviderInstance.getRefreshTokenPromise();
    if (refreshTokenResult) {
        return await curriedResultFn();
    }
    store.dispatch(setIsLogin(false));
    window.location.hash = '/';
    return {errors: 'Вы не авторизованы.'};
};

export const getRequest = async (url, options = {}) => {
    try {
        const storageToken = new StorageToken();
        const accessToken = storageToken.getToken();
        const res = await fetch(url, {
            method: 'GET',
            mode: 'cors',
            headers: {'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}`},
        });
        const response = await res.json();
        if (!res.ok) {
            switch (res.status) {
                case 403:
                    const curriedResultFn = async () => { return await getRequest(url, options); };
                    return await tryRefreshToken(refreshTokenProviderInstance, curriedResultFn);
                default:
                    return {errors: response.errors};
            }
        }
        if (options.detail) {
            const educationDocumentsDetail = [];
            for (const educationDocument of response) {
                const educationDocumentDetail = await getRequest(
                    `${options.apiDomainVikon}/api/digital_profile/applicant/documents/${educationDocument.id}`,
                );
                if (educationDocumentDetail) {
                    educationDocumentsDetail.push(educationDocumentDetail);
                }
            }
            return educationDocumentsDetail;
        }
        return response;
    } catch (e) {
        return {errors: e.message};
    }
};

const NOT_TEMPORARY_FILE_STATUS = 0;
export class FilemanagerApi {
    constructor(apiDomainFilemanager) {
        this._apiPrefix = `${apiDomainFilemanager}api`;
        this._defaultFetchOptions = {
            mode: 'cors',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        };
    }

    async uploadFile(file, organizationId) {
        const storageToken = new StorageToken();
        const accessToken = storageToken.getToken();
        const data = new FormData();
        data.append('token', accessToken);
        data.append('vuz_id', organizationId);
        data.append('file', file);
        const url = `${this._apiPrefix}/applicants/files/upload`;
        try {
            const res = await fetch(url, {
                method: 'POST',
                body: data,
                mode: 'cors',
                headers: {},
            });
            const response = await res.json();
            if (!res.ok) {
                switch (res.status) {
                    case 422:
                        return {error: response.messages.join('')};
                    case 401:
                        const curriedResultFn = async () => { return await this.uploadFile(file, organizationId); };
                        return await tryRefreshToken(refreshTokenProviderInstance, curriedResultFn);
                    default:
                        return {error: response.error};
                }
            }
            return response;
        } catch (e) {
            const sentry = new sentryInstance();
            sentry.captureMessage(`${e.message} (organizationId: ${organizationId}, url: ${url}, file: ${file.name} ${file.size} ${file.type})`);
            return {error: 'Ошибка при загрузке файлов'};
        }
    }

    async deleteFile(fileId) {
        const storageToken = new StorageToken();
        const accessToken = storageToken.getToken();
        const data = {
            token: accessToken,
        };
        const url = `${this._apiPrefix}/applicants/files/${fileId}`;
        const res = await fetch(url, {
            method: 'DELETE',
            body: queryString.stringify(data, {arrayFormat: 'bracket'}),
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        });
        const response = await res.json();
        if (!res.ok) {
            switch (res.status) {
                case 422:
                    return {error: response.error};
                case 401:
                    const curriedResultFn = async () => { return await this.deleteFile(fileId); };
                    return await tryRefreshToken(refreshTokenProviderInstance, curriedResultFn);
                default:
                    return {error: response.error};
            }
        }
        return response;
    }

    async changeFilesNotTemporaryStatus(fileIds) {
        const storageToken = new StorageToken();
        const accessToken = storageToken.getToken();
        const data = {
            token: accessToken,
            file_ids: fileIds,
            temporary: NOT_TEMPORARY_FILE_STATUS,
        };
        const url = `${this._apiPrefix}/applicants/files/status`;
        const res = await fetch(url, {
            method: 'PUT',
            body: queryString.stringify(data, {arrayFormat: 'bracket'}),
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        });
        const response = await res.json();
        if (!res.ok) {
            switch (res.status) {
                case 422:
                    return {error: response.error};
                case 401:
                    const curriedResultFn = async () => { return await this.changeFilesNotTemporaryStatus(fileIds); };
                    return await tryRefreshToken(refreshTokenProviderInstance, curriedResultFn);
                case 404:
                    return [];
                default:
                    return {error: response.error};
            }
        }
        return response;
    }

    async deleteFiles(fileIds) {
        const storageToken = new StorageToken();
        const accessToken = storageToken.getToken();
        const data = {
            token: accessToken,
            file_ids: fileIds,
        };
        const url = `${this._apiPrefix}/applicants/files`;
        const res = await fetch(url, {
            method: 'DELETE',
            body: queryString.stringify(data, {arrayFormat: 'bracket'}),
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        });
        const response = await res.json();
        if (!res.ok) {
            switch (res.status) {
                case 422:
                    return {error: response.error};
                case 401:
                    const curriedResultFn = async () => { return await this.deleteFiles(fileIds); };
                    return await tryRefreshToken(refreshTokenProviderInstance, curriedResultFn);
                default:
                    return {error: response.error};
            }
        }
        return response;
    }

    async getFilesInfo(fileIds) {
        const storageToken = new StorageToken();
        const accessToken = storageToken.getToken();
        const data = {
            file_ids: fileIds,
            token: accessToken,
        };
        const url = `${this._apiPrefix}/applicants/files?${queryString.stringify(data, {arrayFormat: 'bracket'})}`;
        const res = await fetch(url, {...this._defaultFetchOptions, method: 'GET'});
        const response = await res.json();
        if (!res.ok) {
            switch (res.status) {
                case 422:
                    return {error: response.error};
                case 401:
                    const curriedResultFn = async () => { return await this.getFilesInfo(fileIds); };
                    return await tryRefreshToken(refreshTokenProviderInstance, curriedResultFn);
                case 404:
                    return [];
                default:
                    return {error: response.error};
            }
        }
        return response;
    }

    async downloadFile(fileId) {
        try {
            const storageToken = new StorageToken();
            const accessToken = storageToken.getToken();
            const data = {
                token: accessToken,
            };
            const href = `${this._apiPrefix}/applicants/files/${fileId}/download?${queryString.stringify(data, {arrayFormat: 'bracket'})}`;
            const link = document.createElement('a');
            link.setAttribute('href', href);
            link.setAttribute('download', 'download');
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (e) {
            return {errors: e.message};
        }
    }
}

export const saveRequest = async (url, data) => {
    try {
        const storageToken = new StorageToken();
        const accessToken = storageToken.getToken();
        const res = await fetch(url, {
            method: data.id ? 'PUT' : 'POST',
            mode: 'cors',
            body: queryString.stringify(data, {arrayFormat: 'bracket'}),
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/x-www-form-urlencoded',
                Authorization: `Bearer ${accessToken}`,
                'App-Language': LanguageService.getLang() || 'ru'
            },
        });
        const response = await res.json();
        if (!res.ok) {
            switch (res.status) {
                case 403:
                    const curriedResultFn = async () => { return await saveRequest(url, data); };
                    return await tryRefreshToken(refreshTokenProviderInstance, curriedResultFn);
                case 400:
                case 422:
                    return {errors: response.errors};
                default:
                    return {errors: response.errors};
            }
        }
        return response;
    } catch (e) {
        return {errors: e.message};
    }
};

export const removeListRequest = async (url, list) => {
    let success = true;
    for (const id of list) {
        const removedEducationDocument = await removeRequest(url, id);
        if (!removedEducationDocument) {
            success = false;
        }
    }
    return success ? '' : 'Ошибка при удалении.';
};

export const removeRequest = async (url, id) => {
    try {
        const storageToken = new StorageToken();
        const accessToken = storageToken.getToken();
        const res = await fetch(`${url}/${id}`, {
            method: 'DELETE',
            mode: 'cors',
            headers: {'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}`},
        });
        if (!res.ok) {
            switch (res.status) {
                case 403:
                    const curriedResultFn = async () => { return await removeRequest(url, id); };
                    return await tryRefreshToken(refreshTokenProviderInstance, curriedResultFn);
                default:
                    return false;
            }
        }
        return true;
    } catch (e) {
        return false;
    }
};
